import React, { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@hooks/index';
import { PAGE_SIZE } from '@constants/common.constants';
import {
    HISTORICAL_DOCUMENTS_DEFAULT_SORT_COLUMN,
    HISTORICAL_DOCUMENTS_DEFAULT_SORT_ORDER,
    HISTORICAL_DOCUMENTS_INITIAL_PAGE_NUMBER,
} from '../constants';
import type { ICapHistoricalDocumentsFetchRequest } from './cap.types';
import {
    fetchCapHistoricalDocuments,
    resetCapHistoricalDocuments,
} from '@actions/cap/capHistoricalDocumentsActionCreators';
import ConditionalRendering from '@components/common/conditional-rendering/ConditionalRendering';
import HistoricalDocumentsView from '../components/HistoricalDocumentsView';

const CapHistoricalDocumentsContainer: React.FC = () => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const userRefId = searchParams.get('referenceId') ?? '';

    const {
        hasLoaded,
        documents,
        totalCount,
        pageNumber,
        pageSize,
        sortColumn,
        sortOrder,
        hasLoadError,
    } = useAppSelector(state => state.capHistoricalDocuments);

    const handleFetch = useCallback(
        (options: Partial<ICapHistoricalDocumentsFetchRequest>) => {
            if (!userRefId) {
                return;
            }
            dispatch(fetchCapHistoricalDocuments({ ...options, userRefId }));
        },
        [dispatch, userRefId],
    );

    useEffect(() => {
        if (!userRefId) {
            return;
        }
        handleFetch({
            pageNumber: HISTORICAL_DOCUMENTS_INITIAL_PAGE_NUMBER,
            pageSize: PAGE_SIZE,
            sortColumn: HISTORICAL_DOCUMENTS_DEFAULT_SORT_COLUMN,
            sortOrder: HISTORICAL_DOCUMENTS_DEFAULT_SORT_ORDER,
        });
    }, [handleFetch, userRefId]);

    useEffect(() => {
        return () => {
            dispatch(resetCapHistoricalDocuments());
        };
    }, [dispatch]);

    return (
        <ConditionalRendering isTrue={hasLoaded}>
            <HistoricalDocumentsView
                tableId='historical-documents-table'
                documents={documents}
                totalCount={totalCount}
                pageNumber={pageNumber}
                pageSize={pageSize}
                sortColumn={sortColumn}
                sortOrder={sortOrder}
                hasLoadError={hasLoadError}
                onFetch={handleFetch}
            />
        </ConditionalRendering>
    );
};

export default CapHistoricalDocumentsContainer;
